export type CheckoutLaneSelection = {
  readonly tierSkuId: string;
  readonly laneIds: string[];
  readonly laneLabels: string[];
};

const STORAGE_KEY = 'cybard.checkout.lane-selections.v1';

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeSelection(value: unknown): CheckoutLaneSelection | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const tierSkuId = normalizeString(record.tierSkuId);
  if (!tierSkuId) return null;

  const laneIds = Array.from(
    new Set(
      (Array.isArray(record.laneIds) ? record.laneIds : [])
        .map((entry) => normalizeString(entry))
        .filter(Boolean),
    ),
  );
  const laneLabels = Array.from(
    new Set(
      (Array.isArray(record.laneLabels) ? record.laneLabels : [])
        .map((entry) => normalizeString(entry))
        .filter(Boolean),
    ),
  );

  return {
    tierSkuId,
    laneIds,
    laneLabels,
  };
}

export function readCheckoutLaneSelections(): CheckoutLaneSelection[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => normalizeSelection(entry))
      .filter((entry): entry is CheckoutLaneSelection => Boolean(entry));
  } catch {
    return [];
  }
}

export function writeCheckoutLaneSelections(
  selections: readonly CheckoutLaneSelection[],
): void {
  const normalized = selections
    .map((entry) => normalizeSelection(entry))
    .filter((entry): entry is CheckoutLaneSelection => Boolean(entry));

  if (!normalized.length) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
}

export function upsertCheckoutLaneSelection(selection: CheckoutLaneSelection): void {
  const normalized = normalizeSelection(selection);
  if (!normalized) return;
  const existing = readCheckoutLaneSelections().filter(
    (entry) => entry.tierSkuId !== normalized.tierSkuId,
  );
  writeCheckoutLaneSelections([...existing, normalized]);
}

export function filterCheckoutLaneSelectionsByTierSkuIds(
  tierSkuIds: readonly string[],
): CheckoutLaneSelection[] {
  const allowed = new Set(
    tierSkuIds.map((value) => normalizeString(value)).filter(Boolean),
  );
  if (!allowed.size) return [];
  return readCheckoutLaneSelections().filter((entry) => allowed.has(entry.tierSkuId));
}
